import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { OAuthErrorEvent, OAuthService } from 'angular-oauth2-oidc';
import { filter } from 'rxjs/operators';
import { authConfig } from '../models/auth-config';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AuthAccess {
  private oauthService = inject(OAuthService);
  private router = inject(Router);

  private isAuthenticatedSubject$ = new BehaviorSubject<boolean>(false);
  public isAuthenticated$ = this.isAuthenticatedSubject$.asObservable();

  private isDoneLoadingSubject$ = new BehaviorSubject<boolean>(false);
  public isDoneLoading$ = this.isDoneLoadingSubject$.asObservable();

  constructor() {
    // Useful for debugging:
    /*this.oauthService.events.subscribe(event => {
      if (event instanceof OAuthErrorEvent) {
        console.error('OAuthErrorEvent Object:', event);
      } else {
        console.warn('OAuthEvent Object:', event);
      }
    });*/

    // THe following cross-tab communication of fresh access tokens works usually in practice,
    // but if you need more robust handling the community has come up with ways to extend logic
    // in the library which may give you better mileage.
    //
    // See: https://github.com/jeroenheijmans/sample-angular-oauth2-oidc-with-auth-guards/issues/2
    //
    // Until then we'll stick to this:
    window.addEventListener('storage', event => {
      // The `key` is `null` if the event was caused by `.clear()`
      if (event.key !== 'access_token' && event.key !== null) {
        return;
      }

      /*console.warn(
        'Noticed changes to access_token (most likely from another tab), updating isAuthenticated'
      );*/
      this.isAuthenticatedSubject$.next(
        this.oauthService.hasValidAccessToken()
      );
    });

    this.oauthService.events.subscribe(() => {
      this.isAuthenticatedSubject$.next(
        this.oauthService.hasValidAccessToken()
      );
    });
    this.isAuthenticatedSubject$.next(this.oauthService.hasValidAccessToken());

    this.oauthService.events
      .pipe(filter(e => ['token_received'].includes(e.type)))
      .subscribe(() => this.oauthService.loadUserProfile());

    this.oauthService.setupAutomaticSilentRefresh();

    // A failed automatic refresh would otherwise leave the page without a
    // token until it is reloaded.
    this.oauthService.events
      .pipe(filter(e => e.type === 'token_refresh_error'))
      .subscribe(event => {
        if (this.isDoneLoadingSubject$.value) {
          this.handleRefreshError((event as OAuthErrorEvent).reason);
        }
      });

    // Browsers pause or throttle the refresh timer while the tab is in the
    // background or the computer sleeps, so the token may have expired by the
    // time the user comes back.
    document.addEventListener('visibilitychange', () => {
      if (
        document.visibilityState === 'visible' &&
        this.isDoneLoadingSubject$.value
      ) {
        this.refreshIfExpired();
      }
    });
  }

  private refreshInProgress?: Promise<unknown>;

  private refreshIfExpired() {
    if (
      this.refreshInProgress ||
      this.oauthService.hasValidAccessToken() ||
      !this.oauthService.getRefreshToken()
    ) {
      return;
    }
    // Errors are handled by the token_refresh_error subscription.
    this.refreshInProgress = this.oauthService
      .refreshToken()
      .catch(() => undefined)
      .finally(() => (this.refreshInProgress = undefined));
  }

  /**
   * The identity provider rejecting the refresh token (4xx) means only a new
   * login helps. Network errors are left alone, the next return to the tab
   * tries again.
   */
  private handleRefreshError(reason: unknown) {
    if (
      reason instanceof HttpErrorResponse &&
      reason.status >= 400 &&
      reason.status < 500
    ) {
      this.login();
    }
  }

  public runInitialLoginSequence(): Promise<void> {
    this.oauthService.configure(authConfig);

    // 0. LOAD CONFIG:
    // First we have to check to see how the IdServer is
    // currently configured:
    return this.oauthService
      .loadDiscoveryDocumentAndTryLogin()
      .then(() => {
        if (
          this.oauthService.hasValidAccessToken() ||
          !this.oauthService.getRefreshToken()
        ) {
          return Promise.resolve();
        }

        // 2. REFRESH TOKEN LOGIN:
        // The access token has expired, but the refresh token can still get a
        // new one without redirecting the user. If it is rejected, the auth
        // guard sends the user to the login.
        return this.oauthService
          .refreshToken()
          .then(() => undefined)
          .catch(() => undefined);
      })

      .then(() => {
        this.isDoneLoadingSubject$.next(true);

        // Check for the strings 'undefined' and 'null' just to be sure. Our current
        // login(...) should never have this, but in case someone ever calls
        // initImplicitFlow(undefined | null) this could happen.
        if (
          this.oauthService.state &&
          this.oauthService.state !== 'undefined' &&
          this.oauthService.state !== 'null'
        ) {
          let stateUrl = this.oauthService.state;
          if (stateUrl.startsWith('/') === false) {
            stateUrl = decodeURIComponent(stateUrl);
          }
          this.router.navigateByUrl(stateUrl);
        }
      })
      .catch(() => {
        // If something went wrong that we couldn't handle,
        // still mark loading as done to unblock the UI.
        this.isDoneLoadingSubject$.next(true);
      });
  }

  public login(targetUrl?: string) {
    this.oauthService.initLoginFlow(targetUrl || this.router.url);
  }

  public logout() {
    this.oauthService.logOut();
  }
  public refresh() {
    this.oauthService.refreshToken();
  }
  public hasValidToken() {
    return this.oauthService.hasValidAccessToken();
  }
  public get accessToken() {
    return this.oauthService.getAccessToken();
  }
  public get refreshToken() {
    return this.oauthService.getRefreshToken();
  }
  public get identityClaims() {
    return this.oauthService.getIdentityClaims();
  }
  public get idToken() {
    return this.oauthService.getIdToken();
  }
  public get logoutUrl() {
    return this.oauthService.logoutUrl;
  }
}
